/**
 * Kleinfeld Again AI Bridal Consultant Widget — "ASK Dorothy"
 *
 * Self-contained floating chat widget for kleinfeldagain.com Shopify storefront.
 * Connects to the Dorothy AI Assistant SSE backend. No external dependencies.
 *
 * Usage: Add to Shopify theme assets, then in theme.liquid before </body>:
 *   <script src="{{ 'dorothy-stylist.js' | asset_url }}" defer></script>
 */
(function () {
  "use strict";

  // ── Configuration ──────────────────────────────────────────────────
  const API_BASE = "https://bridesbyld.com/kleinfeldagain/api/assistant";
  const STORE_URL = "https://kleinfeldagain.com";
  const MAX_MESSAGE_LENGTH = 500;

  // ── State ──────────────────────────────────────────────────────────
  let sessionId = sessionStorage.getItem("dorothy_sessionId") || null;
  let isOpen = false;
  let isStreaming = false;
  let abortController = null;
  let panelBuilt = false;

  let floatingBtn = null;
  let chatPanel = null;
  let messagesContainer = null;
  let chatInput = null;
  let sendBtn = null;
  let typingIndicator = null;
  let currentAssistantBubble = null;
  let streamBuffer = "";
  let localMessageCount = 0;
  let productDataCache = {}; // SSE product data keyed by handle — source of truth for images

  // ── CSS ────────────────────────────────────────────────────────────
  function injectStyles() {
    const style = document.createElement("style");
    style.textContent = `
      /* Floating Button — speech bubble with bottom-left tail */
      .dorothy-float-btn {
        position: fixed;
        bottom: 54px;
        right: 24px;
        z-index: 9999;
        background: #8B6F47;
        color: #fff;
        border: none;
        cursor: pointer;
        font-family: 'Poppins', sans-serif;
        font-weight: 600;
        font-size: 11px;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        padding: 10px 18px;
        border-radius: 16px;
        line-height: 1.3;
        text-align: center;
        box-shadow: 0 4px 16px rgba(0,0,0,0.12);
        transition: background 0.2s, transform 0.2s, box-shadow 0.2s;
        animation: dorothy-fade-in 0.4s ease;
      }
      .dorothy-float-btn::after {
        content: '';
        position: absolute;
        bottom: -8px;
        left: 20px;
        width: 0;
        height: 0;
        border-left: 10px solid #8B6F47;
        border-right: 6px solid transparent;
        border-top: 8px solid #8B6F47;
        border-bottom: 6px solid transparent;
      }
      .dorothy-float-btn:hover {
        background: #7A6040;
        transform: scale(1.04);
        box-shadow: 0 6px 20px rgba(0,0,0,0.16);
      }
      .dorothy-float-btn:hover::after {
        border-left-color: #7A6040;
        border-top-color: #7A6040;
      }
      .dorothy-float-btn.dorothy-hidden { display: none; }

      @keyframes dorothy-fade-in {
        from { opacity: 0; transform: translateY(12px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes dorothy-jump {
        0%, 100% { transform: translateY(0); }
        15% { transform: translateY(-18px); }
        30% { transform: translateY(0); }
        45% { transform: translateY(-12px); }
        60% { transform: translateY(0); }
        72% { transform: translateY(-6px); }
        84% { transform: translateY(0); }
      }
      .dorothy-float-btn.dorothy-bounce {
        animation: dorothy-jump 1s ease 0.3s 3;
      }

      /* Chat Panel */
      .dorothy-panel {
        position: fixed;
        bottom: 54px;
        right: 24px;
        width: 380px;
        height: 550px;
        z-index: 10000;
        display: flex;
        flex-direction: column;
        background: #fff;
        border: 1px solid #e0e0e0;
        box-shadow: 0 8px 32px rgba(0,0,0,0.15);
        animation: dorothy-slide-up 0.25s ease;
        font-family: 'Poppins', sans-serif;
      }
      .dorothy-panel.dorothy-hidden { display: none; }

      @keyframes dorothy-slide-up {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
      }

      /* Header */
      .dorothy-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 14px 16px;
        background: #8B6F47;
      }
      .dorothy-header-title {
        font-weight: 600;
        font-size: 15px;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #fff;
      }
      .dorothy-close-btn {
        background: none;
        border: none;
        cursor: pointer;
        color: #fff;
        font-size: 20px;
        line-height: 1;
        padding: 0 2px;
        transition: opacity 0.2s;
      }
      .dorothy-close-btn:hover { opacity: 0.6; }

      /* Messages */
      .dorothy-messages {
        flex: 1;
        overflow-y: auto;
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 12px;
        background: #fff;
      }
      .dorothy-msg {
        max-width: 82%;
        padding: 10px 14px;
        font-size: 14px;
        line-height: 1.5;
        word-wrap: break-word;
        white-space: pre-wrap;
      }
      .dorothy-msg-user {
        align-self: flex-end;
        background: #8B6F47;
        color: #fff;
        border-radius: 14px 14px 0 14px;
      }
      .dorothy-msg-assistant {
        align-self: flex-start;
        background: #f5f5f5;
        color: #000;
        border-radius: 14px 14px 14px 0;
      }
      .dorothy-msg-system {
        align-self: center;
        background: #fff3f3;
        color: #c00;
        border-radius: 8px;
        font-size: 13px;
        text-align: center;
        padding: 8px 14px;
        border: 1px solid #fcc;
      }

      /* Typing indicator */
      .dorothy-typing {
        align-self: flex-start;
        display: none;
        padding: 10px 14px;
        background: #f5f5f5;
        border-radius: 14px 14px 14px 0;
      }
      .dorothy-typing.dorothy-visible { display: flex; gap: 4px; align-items: center; flex-wrap: wrap; }
      .dorothy-typing-status {
        width: 100%;
        font-size: 12px;
        color: #8B6F47;
        margin-bottom: 4px;
        font-style: italic;
      }
      .dorothy-typing-dot {
        width: 7px;
        height: 7px;
        background: #8B6F47;
        border-radius: 50%;
        animation: dorothy-bounce 1.4s infinite ease-in-out both;
      }
      .dorothy-typing-dot:nth-child(1) { animation-delay: -0.32s; }
      .dorothy-typing-dot:nth-child(2) { animation-delay: -0.16s; }
      @keyframes dorothy-bounce {
        0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
        40% { transform: scale(1); opacity: 1; }
      }

      /* Inline Product Card */
      .dorothy-inline-product {
        display: flex;
        gap: 10px;
        background: #fff;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        padding: 8px;
        margin: 8px 0;
        text-decoration: none;
        color: inherit;
        transition: box-shadow 0.2s;
      }
      .dorothy-inline-product:hover {
        box-shadow: 0 4px 12px rgba(0,0,0,0.1);
      }
      .dorothy-inline-product-img {
        width: 120px;
        height: 180px;
        object-fit: cover;
        border-radius: 4px;
        background: #f5f5f5;
        flex-shrink: 0;
      }
      .dorothy-inline-product-info {
        display: flex;
        flex-direction: column;
        justify-content: center;
        min-width: 0;
      }
      .dorothy-inline-product-vendor {
        font-size: 10px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #888;
        margin-bottom: 2px;
      }
      .dorothy-inline-product-title {
        font-size: 12px;
        font-weight: 500;
        color: #000;
        line-height: 1.3;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        margin-bottom: 4px;
      }
      .dorothy-inline-product-price {
        font-size: 12px;
        font-weight: 600;
        color: #000;
      }
      .dorothy-inline-product-compare {
        font-size: 11px;
        color: #999;
        text-decoration: line-through;
        margin-right: 4px;
        font-weight: 400;
      }
      .dorothy-inline-product-sale {
        color: #c00;
      }
      .dorothy-inline-product-condition {
        font-size: 10px;
        color: #8B6F47;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        margin-top: 2px;
      }

      /* Cart confirmation */
      .dorothy-msg-cart {
        align-self: center;
        background: #f0faf0;
        color: #2d7a3a;
        border-radius: 8px;
        font-size: 13px;
        text-align: center;
        padding: 8px 14px;
        border: 1px solid #c3e6c3;
      }
      .dorothy-msg-cart-error {
        align-self: center;
        background: #fff3f3;
        color: #c00;
        border-radius: 8px;
        font-size: 13px;
        text-align: center;
        padding: 8px 14px;
        border: 1px solid #fcc;
      }

      /* Input Area */
      .dorothy-input-area {
        display: flex;
        align-items: center;
        padding: 10px 12px;
        border-top: 1px solid #e0e0e0;
        background: #fff;
        gap: 8px;
      }
      .dorothy-input {
        flex: 1;
        border: none;
        outline: none;
        font-family: 'Poppins', sans-serif;
        font-size: 14px;
        padding: 8px 4px;
        color: #000;
        background: transparent;
      }
      .dorothy-input::placeholder { color: #aaa; }
      .dorothy-send-btn {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: none;
        background: #8B6F47;
        color: #fff;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        transition: background 0.2s;
      }
      .dorothy-send-btn:hover { background: #7A6040; }
      .dorothy-send-btn:disabled { background: #ccc; cursor: default; }
      .dorothy-send-btn svg {
        width: 16px;
        height: 16px;
        fill: #fff;
      }

      /* Mobile: full-screen panel */
      @media (max-width: 767px) {
        .dorothy-panel {
          bottom: 0;
          right: 0;
          width: 100vw;
          height: 100vh;
          height: 100dvh;
        }
        .dorothy-float-btn {
          bottom: 46px;
          right: 16px;
          padding: 8px 16px;
          font-size: 10px;
        }
      }
    `;
    document.head.appendChild(style);
  }

  // ── Build DOM ──────────────────────────────────────────────────────
  function buildButton() {
    floatingBtn = document.createElement("button");
    floatingBtn.className = "dorothy-float-btn";
    floatingBtn.innerHTML = "ASK<br>DOROTHY";
    floatingBtn.setAttribute("aria-label", "Chat with Dorothy, your bridal consultant");
    floatingBtn.addEventListener("click", openPanel);
    document.body.appendChild(floatingBtn);
  }

  function buildPanel() {
    if (panelBuilt) return;
    panelBuilt = true;

    chatPanel = document.createElement("div");
    chatPanel.className = "dorothy-panel dorothy-hidden";

    var header = document.createElement("div");
    header.className = "dorothy-header";
    var title = document.createElement("span");
    title.className = "dorothy-header-title";
    title.textContent = "ASK DOROTHY";
    var closeBtn = document.createElement("button");
    closeBtn.className = "dorothy-close-btn";
    closeBtn.innerHTML = "&#x2715;";
    closeBtn.setAttribute("aria-label", "Close chat");
    closeBtn.addEventListener("click", closePanel);
    header.appendChild(title);
    header.appendChild(closeBtn);

    messagesContainer = document.createElement("div");
    messagesContainer.className = "dorothy-messages";

    typingIndicator = document.createElement("div");
    typingIndicator.className = "dorothy-typing";
    var typingStatus = document.createElement("div");
    typingStatus.className = "dorothy-typing-status";
    typingIndicator.appendChild(typingStatus);
    for (var i = 0; i < 3; i++) {
      var dot = document.createElement("div");
      dot.className = "dorothy-typing-dot";
      typingIndicator.appendChild(dot);
    }
    messagesContainer.appendChild(typingIndicator);

    var inputArea = document.createElement("div");
    inputArea.className = "dorothy-input-area";
    chatInput = document.createElement("input");
    chatInput.className = "dorothy-input";
    chatInput.type = "text";
    chatInput.placeholder = "Ask Dorothy...";
    chatInput.maxLength = MAX_MESSAGE_LENGTH;
    chatInput.addEventListener("keydown", function (e) {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    });
    sendBtn = document.createElement("button");
    sendBtn.className = "dorothy-send-btn";
    sendBtn.setAttribute("aria-label", "Send message");
    sendBtn.innerHTML =
      '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>';
    sendBtn.addEventListener("click", handleSend);
    inputArea.appendChild(chatInput);
    inputArea.appendChild(sendBtn);

    chatPanel.appendChild(header);
    chatPanel.appendChild(messagesContainer);
    chatPanel.appendChild(inputArea);
    document.body.appendChild(chatPanel);

    var savedHtml = sessionStorage.getItem("dorothy_messages");
    if (savedHtml) {
      var temp = document.createElement("div");
      temp.innerHTML = savedHtml;
      while (temp.firstChild) {
        messagesContainer.insertBefore(temp.firstChild, typingIndicator);
      }
      localMessageCount = messagesContainer.querySelectorAll(
        ".dorothy-msg-user, .dorothy-msg-assistant",
      ).length;
      if (sessionStorage.getItem("dorothy_pendingSync") === "1") {
        var assistantMsgs = messagesContainer.querySelectorAll(".dorothy-msg-assistant");
        if (assistantMsgs.length > 0) {
          assistantMsgs[assistantMsgs.length - 1].classList.add("dorothy-msg-partial");
        }
      }
      requestAnimationFrame(function () {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      });
    } else {
      addAssistantMessage(
        "Hi! I'm Dorothy, your bridal consultant at Kleinfeld Again. Whether you're looking for your dream wedding dress, a bridesmaid dress, or the perfect veil \u2014 I'm here to help you find it!",
      );
    }
  }

  function saveConversation() {
    if (!messagesContainer) return;
    var nodes = messagesContainer.querySelectorAll(".dorothy-msg");
    var html = "";
    nodes.forEach(function (n) {
      html += n.outerHTML;
    });
    sessionStorage.setItem("dorothy_messages", html);
  }

  // ── Panel Open / Close ─────────────────────────────────────────────
  function openPanel() {
    buildPanel();
    floatingBtn.classList.add("dorothy-hidden");
    chatPanel.classList.remove("dorothy-hidden");
    isOpen = true;
    sessionStorage.setItem("dorothy_open", "1");
    setTimeout(function () {
      chatInput.focus();
      scrollToBottom();
    }, 100);
  }

  function closePanel() {
    chatPanel.classList.add("dorothy-hidden");
    floatingBtn.classList.remove("dorothy-hidden");
    isOpen = false;
    sessionStorage.setItem("dorothy_open", "0");
    if (abortController) {
      abortController.abort();
      abortController = null;
      isStreaming = false;
      setInputEnabled(true);
      hideTyping();
    }
  }

  // ── Message Rendering ──────────────────────────────────────────────
  function addUserMessage(text) {
    var msg = document.createElement("div");
    msg.className = "dorothy-msg dorothy-msg-user";
    msg.textContent = text;
    messagesContainer.insertBefore(msg, typingIndicator);
    scrollToBottom();
    saveConversation();
  }

  function addAssistantMessage(text) {
    var msg = document.createElement("div");
    msg.className = "dorothy-msg dorothy-msg-assistant";
    msg.textContent = text;
    messagesContainer.insertBefore(msg, typingIndicator);
    scrollToBottom();
    return msg;
  }

  function addSystemMessage(text) {
    var msg = document.createElement("div");
    msg.className = "dorothy-msg dorothy-msg-system";
    msg.textContent = text;
    messagesContainer.insertBefore(msg, typingIndicator);
    scrollToBottom();
  }

  function startAssistantStream() {
    streamBuffer = "";
    currentAssistantBubble = document.createElement("div");
    currentAssistantBubble.className = "dorothy-msg dorothy-msg-assistant";
    messagesContainer.insertBefore(currentAssistantBubble, typingIndicator);
    return currentAssistantBubble;
  }

  function appendToStream(text) {
    if (!currentAssistantBubble) return;
    streamBuffer += text;
    processStreamBuffer(false);
    scrollToBottom();
  }

  function processStreamBuffer(isFinal) {
    if (!currentAssistantBubble) return;
    var tagPattern = /\[PRODUCT[>\]](\{.*?\})\[\/PRODUCT[>\]]/;

    while (true) {
      var match = streamBuffer.match(tagPattern);
      if (!match) break;

      var before = streamBuffer.substring(0, match.index);
      if (before) appendStreamText(before, isFinal);

      try {
        var p = JSON.parse(match[1]);
        // Override with backend data to prevent AI hallucinated URLs
        var cached = productDataCache[p.handle];
        if (cached) {
          if (cached.image) p.image = cached.image;
          if (cached.price) p.price = cached.price;
          if (cached.compareAtPrice) p.compareAtPrice = cached.compareAtPrice;
          if (cached.vendor) p.vendor = cached.vendor;
          if (cached.condition) p.condition = cached.condition;
        }
        var tmp = document.createElement("div");
        tmp.innerHTML = renderInlineProductHtml(p);
        while (tmp.firstChild) {
          currentAssistantBubble.appendChild(tmp.firstChild);
        }
      } catch (e) {}

      streamBuffer = streamBuffer.substring(match.index + match[0].length);
    }

    if (isFinal) {
      if (streamBuffer) {
        appendStreamText(streamBuffer, true);
        streamBuffer = "";
      }
    } else {
      var openIdx = streamBuffer.lastIndexOf("[PRODUCT]");
      if (openIdx >= 0) {
        var safe = streamBuffer.substring(0, openIdx);
        if (safe) appendStreamText(safe, false);
        streamBuffer = streamBuffer.substring(openIdx);
      } else {
        var bracketIdx = streamBuffer.lastIndexOf("[");
        if (bracketIdx >= 0 && "[PRODUCT]".startsWith(streamBuffer.substring(bracketIdx))) {
          var safe2 = streamBuffer.substring(0, bracketIdx);
          if (safe2) appendStreamText(safe2, false);
          streamBuffer = streamBuffer.substring(bracketIdx);
        } else {
          if (streamBuffer) appendStreamText(streamBuffer, false);
          streamBuffer = "";
        }
      }
    }
  }

  function appendStreamText(text, formatted) {
    if (!text || !currentAssistantBubble) return;
    if (formatted) {
      var cleaned = text.replace(/\n{3,}/g, "\n\n").trim();
      if (cleaned) {
        var span = document.createElement("span");
        span.innerHTML = formatMarkdown(cleaned);
        currentAssistantBubble.appendChild(span);
      }
    } else {
      var last = currentAssistantBubble.lastChild;
      if (last && last.nodeType === 3) {
        last.textContent += text;
      } else if (
        last &&
        last.tagName === "SPAN" &&
        last.childNodes.length === 1 &&
        last.firstChild.nodeType === 3 &&
        !last.querySelector("b, a, br")
      ) {
        last.firstChild.textContent += text;
      } else {
        currentAssistantBubble.appendChild(document.createTextNode(text));
      }
    }
  }

  function renderInlineProductHtml(p) {
    var href = STORE_URL + "/products/" + (p.handle || "");
    var isOnSale = p.compareAtPrice && parseFloat(p.compareAtPrice) > parseFloat(p.price);
    var priceHtml = isOnSale
      ? '<span class="dorothy-inline-product-compare">$' +
        escapeHtml(p.compareAtPrice) +
        "</span>" +
        '<span class="dorothy-inline-product-sale">$' +
        escapeHtml(p.price || "0") +
        "</span>"
      : "$" + escapeHtml(p.price || "0");
    var vendorHtml = p.vendor
      ? '<div class="dorothy-inline-product-vendor">' + escapeHtml(p.vendor) + "</div>"
      : "";
    var conditionHtml = p.condition
      ? '<div class="dorothy-inline-product-condition">' + escapeHtml(p.condition) + "</div>"
      : "";
    return (
      '<a href="' +
      escapeHtml(href) +
      '" target="_self" class="dorothy-inline-product">' +
      '<img src="' +
      escapeHtml(p.image || "") +
      '" alt="' +
      escapeHtml(p.title || "") +
      '" class="dorothy-inline-product-img" loading="lazy">' +
      '<div class="dorothy-inline-product-info">' +
      vendorHtml +
      '<div class="dorothy-inline-product-title">' +
      escapeHtml(p.title || "") +
      "</div>" +
      '<div class="dorothy-inline-product-price">' +
      priceHtml +
      "</div>" +
      conditionHtml +
      "</div></a>"
    );
  }

  // ── Helpers ────────────────────────────────────────────────────────
  function showTyping(statusText) {
    var statusEl = typingIndicator.querySelector(".dorothy-typing-status");
    if (statusEl) statusEl.textContent = statusText || "";
    typingIndicator.classList.add("dorothy-visible");
    scrollToBottom();
  }
  function hideTyping() {
    typingIndicator.classList.remove("dorothy-visible");
    var statusEl = typingIndicator.querySelector(".dorothy-typing-status");
    if (statusEl) statusEl.textContent = "";
  }

  function scrollToBottom() {
    var distFromBottom =
      messagesContainer.scrollHeight - messagesContainer.scrollTop - messagesContainer.clientHeight;
    if (distFromBottom < 150) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  }

  function setInputEnabled(enabled) {
    chatInput.disabled = !enabled;
    sendBtn.disabled = !enabled;
  }

  function escapeHtml(text) {
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function formatMarkdown(text) {
    var safe = escapeHtml(text);
    safe = safe.replace(/\*\*(.+?)\*\*/g, "<b>$1</b>");
    safe = safe.replace(
      /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" style="color:#8B6F47;text-decoration:underline">$1</a>',
    );
    safe = safe.replace(/\n/g, "<br>");
    return safe;
  }

  // ── Cart ───────────────────────────────────────────────────────────
  var variantCache = {};

  function getShopifyRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  }

  async function fetchProductVariants(handle) {
    if (variantCache[handle]) return variantCache[handle];
    var root = getShopifyRoot();
    var res = await fetch(root + "products/" + handle + ".js");
    if (!res.ok) throw new Error("Product fetch failed");
    var product = await res.json();
    variantCache[handle] = product;
    return product;
  }

  var SIZE_ALIASES = {
    "extra small": "XS",
    "x-small": "XS",
    xsmall: "XS",
    small: "S",
    medium: "M",
    med: "M",
    large: "L",
    "extra large": "XL",
    "x-large": "XL",
    xlarge: "XL",
    "extra extra large": "XXL",
    "xx-large": "XXL",
    xxlarge: "XXL",
    "2xl": "XXL",
    xxxl: "XXXL",
    "3xl": "XXXL",
    "one size": "OS",
    "o/s": "OS",
    onesize: "OS",
  };

  function fuzzyMatchOption(requested, actual) {
    if (!requested || !actual) return false;
    var r = requested.trim().toLowerCase();
    var a = actual.trim().toLowerCase();
    if (r === a) return true;
    if (SIZE_ALIASES[r] && SIZE_ALIASES[r].toLowerCase() === a) return true;
    if (SIZE_ALIASES[a] && SIZE_ALIASES[a].toLowerCase() === r) return true;
    if (a.includes(r) || r.includes(a)) return true;
    return false;
  }

  function findMatchingVariant(product, selectedOptions) {
    var variants = product.variants || [];
    var options = product.options || [];

    if (!selectedOptions || Object.keys(selectedOptions).length === 0) {
      return (
        variants.find(function (v) {
          return v.available;
        }) || null
      );
    }

    return (
      variants.find(function (v) {
        if (!v.available) return false;
        return Object.keys(selectedOptions).every(function (optName) {
          var requestedVal = selectedOptions[optName];
          for (var i = 0; i < options.length; i++) {
            if (options[i].name.toLowerCase() === optName.toLowerCase()) {
              var actualVal = v["option" + (i + 1)];
              return fuzzyMatchOption(requestedVal, actualVal);
            }
          }
          return (
            fuzzyMatchOption(requestedVal, v.option1) ||
            fuzzyMatchOption(requestedVal, v.option2) ||
            fuzzyMatchOption(requestedVal, v.option3)
          );
        });
      }) || null
    );
  }

  function addCartMessage(text) {
    var msg = document.createElement("div");
    msg.className = "dorothy-msg dorothy-msg-cart";
    msg.textContent = text;
    messagesContainer.insertBefore(msg, typingIndicator);
    scrollToBottom();
    saveConversation();
  }

  function addCartErrorMessage(text) {
    var msg = document.createElement("div");
    msg.className = "dorothy-msg dorothy-msg-cart-error";
    msg.textContent = text;
    messagesContainer.insertBefore(msg, typingIndicator);
    scrollToBottom();
    saveConversation();
  }

  async function handleCartAdd(data) {
    var handle = data.handle;
    var selectedOptions = data.selectedOptions || {};

    try {
      var product = await fetchProductVariants(handle);
      var variant = findMatchingVariant(product, selectedOptions);

      if (!variant) {
        addCartErrorMessage("That option isn't available right now.");
        return;
      }

      var root = getShopifyRoot();
      var res = await fetch(root + "cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] }),
      });

      if (!res.ok) throw new Error("Cart add failed");

      var variantLabel =
        variant.title && variant.title !== "Default Title" ? " (" + variant.title + ")" : "";
      addCartMessage("\u2713 " + product.title + variantLabel + " added to your bag!");
      refreshCartBadge();
    } catch (err) {
      addCartErrorMessage("Couldn't add to bag. Please try again.");
    }
  }

  function refreshCartBadge() {
    try {
      document.dispatchEvent(new CustomEvent("cart:refresh"));
      var root = getShopifyRoot();
      fetch(root + "cart.js")
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          if (window.Shopify && window.Shopify.onCartUpdate) {
            window.Shopify.onCartUpdate(cart);
          }
          var badge = document.querySelector(
            ".cart-count, .cart-count-bubble, [data-cart-count], .cart-count-badge",
          );
          if (badge) badge.textContent = cart.item_count;
        });
    } catch (e) {}
  }

  // ── Session Management ─────────────────────────────────────────────
  async function ensureSession() {
    if (sessionId) return sessionId;
    try {
      var res = await fetch(API_BASE + "/session/new", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      var data = await res.json();
      if (data.success && data.data && data.data.sessionId) {
        sessionId = data.data.sessionId;
        sessionStorage.setItem("dorothy_sessionId", sessionId);
        return sessionId;
      }
      throw new Error("Invalid session response");
    } catch (err) {
      throw new Error("Could not start session");
    }
  }

  // ── Current Product Detection ──────────────────────────────────────
  function getCurrentProduct() {
    try {
      var path = window.location.pathname;
      var match = path.match(/\/products\/([^/?#]+)/);
      if (!match) return null;
      var handle = match[1];
      var info = { handle: handle };

      if (
        window.ShopifyAnalytics &&
        window.ShopifyAnalytics.meta &&
        window.ShopifyAnalytics.meta.product
      ) {
        var sp = window.ShopifyAnalytics.meta.product;
        if (sp.type) info.productType = sp.type;
        if (sp.vendor) info.vendor = sp.vendor;
      }

      if (window.meta && window.meta.product) {
        if (window.meta.product.vendor) info.vendor = window.meta.product.vendor;
        if (window.meta.product.type) info.productType = window.meta.product.type;
      }

      var titleEl = document.querySelector(
        ".product__title, .product-single__title, h1.title, [data-product-title], .product-title",
      );
      if (titleEl && titleEl.textContent) {
        info.title = titleEl.textContent.trim();
      }

      if (!info.title) {
        var scriptEl = document.querySelector(
          'script[type="application/json"][data-product-json], script[type="application/json"]#ProductJson-product-template',
        );
        if (scriptEl) {
          try {
            var pd = JSON.parse(scriptEl.textContent);
            if (pd.title) info.title = pd.title;
            if (pd.vendor) info.vendor = pd.vendor;
            if (pd.type) info.productType = pd.type;
            if (pd.price) info.price = (pd.price / 100).toFixed(2);
          } catch (e) {}
        }
      }

      if (!info.price) {
        var priceEl = document.querySelector(
          ".product__price .price-item--regular, .product-single__price, [data-product-price], .price--regular .money, .product-price",
        );
        if (priceEl && priceEl.textContent) {
          info.price = priceEl.textContent.trim().replace(/[^0-9.]/g, "");
        }
      }

      if (!info.title) {
        info.title = handle.replace(/-/g, " ").replace(/\b\w/g, function (c) {
          return c.toUpperCase();
        });
      }

      return info;
    } catch (e) {
      return null;
    }
  }

  // ── Send Message ───────────────────────────────────────────────────
  async function handleSend() {
    var text = (chatInput.value || "").trim();
    if (!text || isStreaming) return;

    chatInput.value = "";
    addUserMessage(text);
    setInputEnabled(false);
    isStreaming = true;
    showTyping();

    try {
      await ensureSession();
    } catch (err) {
      hideTyping();
      addSystemMessage("Could not connect. Please try again.");
      setInputEnabled(true);
      isStreaming = false;
      return;
    }

    abortController = new AbortController();

    try {
      var payload = { sessionId: sessionId, message: text, pageUrl: window.location.href };
      var cp = getCurrentProduct();
      if (cp) payload.currentProduct = cp;

      var res = await fetch(API_BASE + "/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: abortController.signal,
      });

      if (res.status === 429) {
        hideTyping();
        addSystemMessage("Dorothy is busy right now. Try again in a moment.");
        setInputEnabled(true);
        isStreaming = false;
        return;
      }

      if (!res.ok || !res.body) {
        throw new Error("Bad response");
      }

      await parseSSEStream(res.body);
    } catch (err) {
      if (err.name === "AbortError") return;
      hideTyping();

      if (err.message && err.message.includes("session")) {
        sessionId = null;
        sessionStorage.removeItem("dorothy_sessionId");
        addSystemMessage("Session expired. Please send your message again.");
      } else {
        addSystemMessage("Connection lost. Please try again.");
      }
    } finally {
      abortController = null;
      isStreaming = false;
      setInputEnabled(true);
      chatInput.focus();
    }
  }

  // ── SSE Stream Parser ─────────────────────────────────────────────
  async function parseSSEStream(body) {
    var reader = body.getReader();
    var decoder = new TextDecoder();
    var buffer = "";
    var currentEvent = "";
    var streamStarted = false;

    while (true) {
      var result = await reader.read();
      if (result.done) break;

      buffer += decoder.decode(result.value, { stream: true });
      var lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (var i = 0; i < lines.length; i++) {
        var line = lines[i].trim();
        if (!line) continue;

        if (line.startsWith("event: ")) {
          currentEvent = line.substring(7);
        } else if (line.startsWith("data: ")) {
          var jsonStr = line.substring(6);
          try {
            var data = JSON.parse(jsonStr);

            if (currentEvent === "text" && !streamStarted) {
              streamStarted = true;
              hideTyping();
              startAssistantStream();
            }

            handleSSEEvent(currentEvent, data);
          } catch (e) {}
        }
      }
    }
  }

  function handleSSEEvent(event, data) {
    switch (event) {
      case "session":
        if (data.sessionId) {
          sessionId = data.sessionId;
          sessionStorage.setItem("dorothy_sessionId", sessionId);
        }
        break;

      case "text":
        appendToStream(data.text || "");
        break;

      case "status":
        showTyping(data.message || "Working on it...");
        break;

      case "products":
        // Cache product data from backend — source of truth for images/prices
        if (Array.isArray(data)) {
          data.forEach(function (p) {
            if (p.handle) productDataCache[p.handle] = p;
          });
        }
        break;

      case "cart_add":
        handleCartAdd(data);
        break;

      case "error":
        hideTyping();
        if (data.error && data.error.toLowerCase().includes("session")) {
          sessionId = null;
          sessionStorage.removeItem("dorothy_sessionId");
        }
        addSystemMessage(data.error || "Something went wrong.");
        break;

      case "done":
        hideTyping();
        if (currentAssistantBubble) {
          processStreamBuffer(true);
          var childNodes = Array.from(currentAssistantBubble.childNodes);
          for (var j = 0; j < childNodes.length; j++) {
            var node = childNodes[j];
            if (node.nodeType === 3 && node.textContent.trim()) {
              var span = document.createElement("span");
              var cleaned = node.textContent.replace(/\n{3,}/g, "\n\n").trim();
              span.innerHTML = formatMarkdown(cleaned);
              currentAssistantBubble.replaceChild(span, node);
            }
          }
        }
        currentAssistantBubble = null;
        streamBuffer = "";
        localMessageCount += 2;
        saveConversation();
        break;
    }
  }

  // ── Sync Missed Messages ────────────────────────────────────────────
  function syncMissedMessages() {
    if (!sessionId) return;
    var count = parseInt(sessionStorage.getItem("dorothy_messageCount") || "0", 10);
    var attempts = 0;
    var maxAttempts = 5;

    function poll() {
      fetch(API_BASE + "/session/" + sessionId + "/sync?messageCount=" + count)
        .then(function (res) {
          return res.json();
        })
        .then(function (data) {
          if (!data.success) return;

          if (data.status === "generating") {
            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(poll, 2000);
            } else {
              sessionStorage.removeItem("dorothy_pendingSync");
              sessionStorage.removeItem("dorothy_messageCount");
              buildPanel();
              var timedOutPartial = messagesContainer.querySelector(".dorothy-msg-partial");
              if (timedOutPartial) timedOutPartial.remove();
              saveConversation();
            }
            return;
          }

          sessionStorage.removeItem("dorothy_pendingSync");
          sessionStorage.removeItem("dorothy_messageCount");
          buildPanel();

          if (!data.messages || data.messages.length === 0) {
            var stalePartial = messagesContainer.querySelector(".dorothy-msg-partial");
            if (stalePartial) stalePartial.remove();
            saveConversation();
            return;
          }

          var partial = messagesContainer.querySelector(".dorothy-msg-partial");
          if (partial) partial.remove();

          for (var i = 0; i < data.messages.length; i++) {
            var msg = data.messages[i];
            if (msg.role === "assistant" && msg.content) {
              var bubble = addAssistantMessage("");
              var content = msg.content;
              var tagPattern = /\[PRODUCT[>\]](\{.*?\})\[\/PRODUCT[>\]]/g;
              var lastIdx = 0;
              var match;
              while ((match = tagPattern.exec(content)) !== null) {
                var before = content.substring(lastIdx, match.index);
                if (before.trim()) {
                  var s = document.createElement("span");
                  s.innerHTML = formatMarkdown(before.replace(/\n{3,}/g, "\n\n").trim());
                  bubble.appendChild(s);
                }
                try {
                  var p = JSON.parse(match[1]);
                  var tmp = document.createElement("div");
                  tmp.innerHTML = renderInlineProductHtml(p);
                  while (tmp.firstChild) {
                    bubble.appendChild(tmp.firstChild);
                  }
                } catch (e) {}
                lastIdx = match.index + match[0].length;
              }
              var remaining = content.substring(lastIdx);
              if (remaining.trim()) {
                var rs = document.createElement("span");
                rs.innerHTML = formatMarkdown(remaining.replace(/\n{3,}/g, "\n\n").trim());
                bubble.appendChild(rs);
              }
              if (
                bubble.firstChild &&
                bubble.firstChild.nodeType === 3 &&
                !bubble.firstChild.textContent.trim()
              ) {
                bubble.removeChild(bubble.firstChild);
              }
              localMessageCount++;
            }
          }
          saveConversation();
          scrollToBottom();
        })
        .catch(function () {
          sessionStorage.removeItem("dorothy_pendingSync");
          sessionStorage.removeItem("dorothy_messageCount");
        });
    }

    poll();
  }

  // ── Initialize ─────────────────────────────────────────────────────
  function init() {
    injectStyles();
    buildButton();
    sessionStorage.setItem("dorothy_open", "0");

    // Bounce the button if there's an active conversation to remind the customer
    if (sessionStorage.getItem("dorothy_messages")) {
      floatingBtn.classList.add("dorothy-bounce");
      floatingBtn.addEventListener("animationend", function () {
        floatingBtn.classList.remove("dorothy-bounce");
      }, { once: true });
    }

    if (sessionStorage.getItem("dorothy_pendingSync") === "1" && sessionId) {
      syncMissedMessages();
    }

    window.addEventListener("beforeunload", function () {
      if (isStreaming && currentAssistantBubble) {
        processStreamBuffer(true);
        currentAssistantBubble.classList.add("dorothy-msg-partial");
        saveConversation();
        sessionStorage.setItem("dorothy_pendingSync", "1");
        sessionStorage.setItem("dorothy_messageCount", String(localMessageCount));
      }
    });

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        for (var j = 0; j < m.addedNodes.length; j++) {
          var node = m.addedNodes[j];
          if (
            node.nodeType === 1 &&
            (node.classList.contains("cart-drawer") ||
              node.classList.contains("cart-notification") ||
              node.id === "cart-drawer" ||
              node.id === "CartDrawer" ||
              node.tagName === "CART-DRAWER")
          ) {
            if (chatPanel && !chatPanel.classList.contains("dorothy-hidden")) {
              closePanel();
            }
          }
        }
        if (m.type === "attributes" && m.target.nodeType === 1) {
          var t = m.target;
          if (
            (t.classList.contains("cart-drawer") ||
              t.id === "cart-drawer" ||
              t.id === "CartDrawer" ||
              t.tagName === "CART-DRAWER") &&
            (t.classList.contains("active") ||
              t.classList.contains("is-open") ||
              t.hasAttribute("open"))
          ) {
            if (chatPanel && !chatPanel.classList.contains("dorothy-hidden")) {
              closePanel();
            }
          }
        }
      }
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "open"],
    });
  }

  if (typeof requestIdleCallback === "function") {
    requestIdleCallback(init);
  } else {
    setTimeout(init, 200);
  }
})();
